import React, { useState, useMemo } from 'react';
import { 
  DollarSign, TrendingUp, FileText, Download, Filter, Calendar,
  BarChart3, Package, Tag
} from 'lucide-react';
import { 
  startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  subMonths, subDays, format, isWithinInterval, startOfDay, endOfDay
} from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const BillingReport = ({ proposals = [], products = [] }) => {
  const { user } = useAuth();
  
  // Verificar si el usuario tiene permiso para ver márgenes y costos
  const canViewMargins = useMemo(() => {
    if (!user || !user.category || !user.category.permissions) {
      return false; // Por defecto, no mostrar si no hay permisos definidos
    }
    return user.category.permissions['reports.billing.margins'] === true;
  }, [user]);
  const [periodFilter, setPeriodFilter] = useState('currentMonth');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Estados de propuestas a incluir
  const targetStatuses = ['Pagado Anticipo', 'Pago Total'];

  // Calcular rango de fechas según el filtro
  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (periodFilter) {
      case 'currentMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'last30Days':
        start = startOfDay(subDays(now, 30));
        end = endOfDay(now);
        break;
      case 'lastMonth':
        const lastMonthDate = subMonths(now, 1);
        start = startOfMonth(lastMonthDate);
        end = endOfMonth(lastMonthDate);
        break;
      case 'quarter1':
        start = startOfQuarter(new Date(now.getFullYear(), 0, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 0, 1));
        break;
      case 'quarter2':
        start = startOfQuarter(new Date(now.getFullYear(), 3, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 3, 1));
        break;
      case 'quarter3':
        start = startOfQuarter(new Date(now.getFullYear(), 6, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 6, 1));
        break;
      case 'quarter4':
        start = startOfQuarter(new Date(now.getFullYear(), 9, 1));
        end = endOfQuarter(new Date(now.getFullYear(), 9, 1));
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          start = startOfDay(new Date(customStartDate));
          end = endOfDay(new Date(customEndDate));
        } else {
          start = startOfMonth(now);
          end = endOfMonth(now);
        }
        break;
      default:
        start = startOfMonth(now);
        end = endOfMonth(now);
    }

    return { start, end };
  };

  const { start: dateStart, end: dateEnd } = getDateRange();

  // Filtrar propuestas por estado y fecha
  const filteredProposals = useMemo(() => {
    const filtered = proposals.filter(proposal => {
      // Filtrar por estado
      if (!targetStatuses.includes(proposal.status)) {
        return false;
      }

      // Filtrar por fecha (usar approvedDate, approved_date o created_at)
      // Si no hay fecha de aprobación, usar created_at como fallback
      const proposalDate = proposal.approvedDate 
        ? new Date(proposal.approvedDate)
        : (proposal.approved_date 
          ? new Date(proposal.approved_date)
          : (proposal.created_at 
            ? new Date(proposal.created_at) 
            : (proposal.createdAt ? new Date(proposal.createdAt) : null)));
      
      // Si no hay fecha válida, usar la fecha actual como fallback
      // Esto asegura que las propuestas aparezcan en el reporte
      if (!proposalDate || isNaN(proposalDate.getTime())) {
        console.warn('Propuesta sin fecha válida, usando fecha actual:', {
          proposalId: proposal.proposalId || proposal.proposal_id,
          status: proposal.status,
          approvedDate: proposal.approvedDate,
          approved_date: proposal.approved_date,
          created_at: proposal.created_at,
          createdAt: proposal.createdAt
        });
        // Usar fecha actual si no hay fecha válida
        return isWithinInterval(new Date(), { start: dateStart, end: dateEnd });
      }

      return isWithinInterval(proposalDate, { start: dateStart, end: dateEnd });
    });
    
    console.log('Propuestas filtradas:', {
      total: proposals.length,
      conEstadoCorrecto: proposals.filter(p => targetStatuses.includes(p.status)).length,
      filtradas: filtered.length,
      detalles: filtered.map(p => ({
        id: p.proposalId || p.proposal_id,
        status: p.status,
        hasItems: !!(p.items && p.items.length > 0),
        itemsCount: (p.items || []).length
      }))
    });
    
    return filtered;
  }, [proposals, dateStart, dateEnd]);

  // Crear mapa de productos por ID para búsqueda rápida
  const productsMap = useMemo(() => {
    const map = {};
    products.forEach(product => {
      // Mapear tanto por ID numérico como string
      const id = product.id?.toString();
      if (id) {
        map[id] = product;
        map[parseInt(id)] = product; // También mapear como número
      }
    });
    console.log('Mapa de productos:', {
      total: products.length,
      mapeados: Object.keys(map).length,
      productos: products.map(p => ({ id: p.id, name: p.name, category: p.category }))
    });
    return map;
  }, [products]);

  // Agrupar datos por categoría
  const dataByCategory = useMemo(() => {
    const categoryData = {};
    const proposalNumbers = new Set();
    let itemsProcessed = 0;
    let itemsSkipped = 0;
    const skippedItems = [];

    filteredProposals.forEach(proposal => {
      const proposalId = proposal.proposalId || proposal.proposal_id;
      proposalNumbers.add(proposalId);

      const items = proposal.items || [];
      if (items.length === 0) return;

      items.forEach((item, itemIndex) => {
        const productId = item.product_id;
        if (!productId) {
          itemsSkipped++;
          skippedItems.push({ proposalId, itemIndex, reason: 'Sin product_id' });
          return;
        }

        const productIdStr = productId.toString();
        const productIdNum = parseInt(productId);
        const product = productsMap[productIdStr] || productsMap[productIdNum] || productsMap[productId];
        
        if (!product) {
          itemsSkipped++;
          skippedItems.push({ 
            proposalId, 
            itemIndex, 
            productId,
            reason: 'Producto no encontrado',
            availableIds: Object.keys(productsMap).slice(0, 5)
          });
          return;
        }

        const category = item.category_name || product.category || 'Sin categoría';
        const quantity = parseInt(item.quantity || 0);
        const unitPrice = parseFloat(item.price_fob_unit || 0);
        const total = parseFloat(item.total || (unitPrice * quantity));
        const productCost = parseFloat(product.cost || 0);
        const cost = productCost * quantity;
        const margin = total - cost;

        if (!categoryData[category]) {
          categoryData[category] = {
            category,
            proposals: new Set(),
            facturacion: 0,
            costo: 0,
            margen: 0,
            items: []
          };
        }

        categoryData[category].proposals.add(proposalId);
        categoryData[category].facturacion += total;
        categoryData[category].costo += cost;
        categoryData[category].margen += margin;
        categoryData[category].items.push({
          proposalId,
          productName: item.product_name || product.name,
          quantity,
          unitPrice,
          total,
          cost,
          margin
        });
        
        itemsProcessed++;
      });
    });

    console.log('Procesamiento de items:', {
      propuestas: filteredProposals.length,
      itemsProcessed,
      itemsSkipped,
      skippedItems: skippedItems.slice(0, 10), // Mostrar solo los primeros 10
      categorias: Object.keys(categoryData).length
    });

    // Convertir Sets a Arrays y ordenar
    return Object.values(categoryData)
      .map(cat => ({
        ...cat,
        proposalCount: cat.proposals.size,
        proposals: Array.from(cat.proposals).sort(),
        marginPercent: cat.facturacion > 0 ? ((cat.margen / cat.facturacion) * 100) : 0
      }))
      .sort((a, b) => b.facturacion - a.facturacion);
  }, [filteredProposals, productsMap]);

  // Calcular totales
  const totals = useMemo(() => {
    return dataByCategory.reduce((acc, cat) => {
      acc.facturacion += cat.facturacion;
      acc.costo += cat.costo;
      acc.margen += cat.margen;
      acc.proposalCount = Math.max(acc.proposalCount, cat.proposalCount);
      return acc;
    }, {
      facturacion: 0,
      costo: 0,
      margen: 0,
      proposalCount: 0
    });
  }, [dataByCategory]);

  totals.marginPercent = totals.facturacion > 0 
    ? ((totals.margen / totals.facturacion) * 100) 
    : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleExport = () => {
    const headers = canViewMargins 
      ? ['Categoría', 'N° Cotizaciones', 'Facturación', 'Costo', 'Margen', 'Margen %']
      : ['Categoría', 'N° Cotizaciones', 'Facturación'];
    const rows = dataByCategory.map(cat => {
      const baseRow = [
        cat.category,
        cat.proposalCount,
        cat.facturacion.toFixed(2)
      ];
      if (canViewMargins) {
        baseRow.push(
          cat.costo.toFixed(2),
          cat.margen.toFixed(2),
          cat.marginPercent.toFixed(2) + '%'
        );
      }
      return baseRow;
    });

    // Agregar fila de totales
    const totalRow = [
      'TOTAL',
      totals.proposalCount,
      totals.facturacion.toFixed(2)
    ];
    if (canViewMargins) {
      totalRow.push(
        totals.costo.toFixed(2),
        totals.margen.toFixed(2),
        totals.marginPercent.toFixed(2) + '%'
      );
    }
    rows.push(totalRow);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_facturacion_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const periodOptions = [
    { value: 'currentMonth', label: 'Mes Corriente' },
    { value: 'last30Days', label: 'Últimos 30 Días' },
    { value: 'lastMonth', label: 'Mes Pasado' },
    { value: 'quarter1', label: 'Trimestre 1' },
    { value: 'quarter2', label: 'Trimestre 2' },
    { value: 'quarter3', label: 'Trimestre 3' },
    { value: 'quarter4', label: 'Trimestre 4' },
    { value: 'custom', label: 'Rango Personalizado' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              Reporte de Facturación por Categoría
            </h1>
            <p className="text-gray-600 mt-1">
              Cotizaciones: {targetStatuses.join(', ')}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Período: {format(dateStart, 'dd/MM/yyyy')} - {format(dateEnd, 'dd/MM/yyyy')}
            </p>
          </div>
          <button
            onClick={handleExport}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold shadow-sm transition-all duration-300 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-2" />
              Período
            </label>
            <select
              value={periodFilter}
              onChange={(e) => {
                setPeriodFilter(e.target.value);
                setShowCustomRange(e.target.value === 'custom');
              }}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            >
              {periodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {showCustomRange && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Resumen */}
      <div
        className={`grid gap-4 ${canViewMargins ? 'grid-cols-1 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-2'}`}
      >
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Cotizaciones</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{filteredProposals.length}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">Facturación Total</span>
          </div>
          <p className="text-2xl font-bold text-green-900">{formatCurrency(totals.facturacion)}</p>
        </div>
        {canViewMargins && (
          <>
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-red-700">Costo Total</span>
              </div>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(totals.costo)}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Margen Total</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(totals.margen)}</p>
              <p className="text-sm text-purple-700 mt-1">
                ({totals.marginPercent.toFixed(2)}%)
              </p>
            </div>
          </>
        )}
      </div>

      {/* Tabla de datos por categoría */}
      <div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  N° Cotizaciones
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Cotizaciones
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Facturación
                </th>
                {canViewMargins && (
                  <>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Costo
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Margen
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Margen %
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dataByCategory.length === 0 ? (
                <tr>
                  <td colSpan={canViewMargins ? 7 : 4} className="px-6 py-12 text-center text-gray-500">
                    <div className="space-y-2">
                      <p className="font-semibold">No hay datos para mostrar en el período seleccionado</p>
                      <p className="text-sm text-gray-400">
                        Cotizaciones encontradas con estado correcto: {filteredProposals.length}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Abre la consola del navegador (F12) para ver detalles de depuración
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {dataByCategory.map((category, index) => (
                    <tr
                      key={category.category}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-blue-500" />
                          <span className="font-semibold text-gray-900">{category.category}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {category.proposalCount}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {category.proposals.slice(0, 5).map(propId => (
                            <span
                              key={propId}
                              className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded"
                            >
                              {propId}
                            </span>
                          ))}
                          {category.proposals.length > 5 && (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded">
                              +{category.proposals.length - 5}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-700">
                        {formatCurrency(category.facturacion)}
                      </td>
                      {canViewMargins && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-700">
                            {formatCurrency(category.costo)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-purple-700">
                            {formatCurrency(category.margen)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span className={`font-semibold ${
                              category.marginPercent >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {category.marginPercent.toFixed(2)}%
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {/* Fila de totales */}
                  <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">TOTAL</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{filteredProposals.length}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {Array.from(new Set(dataByCategory.flatMap(cat => cat.proposals))).length} únicas
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-green-800">
                      {formatCurrency(totals.facturacion)}
                    </td>
                    {canViewMargins && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-red-800">
                          {formatCurrency(totals.costo)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-purple-800">
                          {formatCurrency(totals.margen)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className={`font-bold ${
                            totals.marginPercent >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {totals.marginPercent.toFixed(2)}%
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BillingReport;

